import React, {
	useLayoutEffect,
	useRef,
	useState
} from "react";
import Part from "@/components/drawing/drawparts/part";
import {
	calculateSponningHeight,
	DRAWING_CONSTANTS
} from "./utils.js";

/**
 * Custom hook to track element size changes for responsive 3D rendering
 * Uses ResizeObserver pattern to update dimensions when window resizes
 *
 * @param {React.RefObject} ref - Ref to the element to track
 * @returns {Array} Array containing current width and height
 */
function useRefSize(ref) {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([ref.current.offsetWidth]);
        }

        window.addEventListener("resize", updateSize);
        updateSize();
        return () => window.removeEventListener("resize", updateSize);
    }, []);
    return size;
}

/**
 * Drawing component that renders a 3D visualization of panel construction
 * Displays layered panel structure with materials, insulation, and edge finishing (sponning)
 * Supports inside/outside views and dynamic perspective switching
 *
 * @param {Object} props - Component props
 * @param {Object} props.properties - Form properties containing panel configuration
 * @param {Object} props.session_product - Current product session data
 * @param {Object} props.products - Available product layers (BU, BI, TL, IS1, IS2)
 * @param {boolean} props.insideview - Whether to show inside view of the panel
 * @param {boolean} props.sidepanel - Whether sidepanel is active (affects layout)
 * @param {string} props.sponningside - Current sponning side (SP_BU or SP_BI)
 * @param {boolean} props.colors - Whether to apply color styling to parts
 * @param {boolean} props.afwerkingactive - Whether edge finishing mode is active (default: false)
 */
const Drawing = ({
    properties,
    session_product: sessionProduct,
    products,
    insideview: insideView,
    sidepanel: sidePanel,
    sponningside: sponningSide,
    colors,
    afwerkingactive: afwerkingActive = false,
}) => {

    // State for toggling dimension display
    const [toonSponning, setToonSponning] = useState(false);

    // Ref and size tracking for responsive rendering
    const blockRef = useRef();
    const [drawBlockWidth] = useRefSize(blockRef);

    // Determine actual inside view based on props and finishing mode
    const insideViewProperty =
        insideView || (afwerkingActive && sponningSide === DRAWING_CONSTANTS.sponningTypes.inner);

    // Static text for dimension reference
    const text = `Afbeelding is gebaseerd op paneelmaat ${DRAWING_CONSTANTS.baseWidthMm}mm bij ${DRAWING_CONSTANTS.baseHeightMm}mm`;

    // Check if this is a two-layer configuration
    const tweelaags = sessionProduct.layer === "tweelaags";

    // Using utilities from utils.js for isolation and sponning calculations

    return (
        <div
            className="col-md-6 overflow-hidden d-flex justify-content-center drawingdiv position-relative"
            ref={blockRef}
        >
            {products.BU && (
                <>
                    <div
                        className={`perspective ${insideViewProperty ? "perspective-inside" : ""} ${toonSponning ? "perspective-side" : ""} align-self-center`}
                    >
                        {/* Inner layer (binnenlaag) - closest to inside */}
                        {products.BI && (
                            <Part
                                properties={properties}
                                product={sessionProduct}
                                drawblockwidth={drawBlockWidth}
                                partname={DRAWING_CONSTANTS.layerNames.innerLayer}
                                colors={colors}
                                sponning={DRAWING_CONSTANTS.sponningTypes.inner}
                                material={properties.bi_material}
                                insideview={insideViewProperty}
                                depth={products.BI ? products.BI.zdimsize : DRAWING_CONSTANTS.defaultLayerDepth}
                                color={
                                    products.BI ? products.BI.color : DRAWING_CONSTANTS.defaultColor
                                }
                                article={products.BI}
                            />
                        )}

                        {/* Second insulation layer - first part (closer to inside) */}
                        {products.IS2 && (
                            <Part
                                properties={properties}
                                product={sessionProduct}
                                drawblockwidth={drawBlockWidth}
                                partname={`${DRAWING_CONSTANTS.layerNames.insulation2} isolatie`}
                                sponning={DRAWING_CONSTANTS.sponningTypes.inner}
                                isolation
                                isolation2
                                insideview={insideViewProperty}
                                singleisolation={properties.TL === 0}
                                depth={
                                    products.IS2
                                        ? calculateSponningHeight(
                                              products.IS2.zdimsize,
                                              DRAWING_CONSTANTS.sponningTypes.inner,
                                              true,
                                              properties,
                                              sessionProduct
                                          )
                                        : DRAWING_CONSTANTS.defaultInsulationDepth
                                }
                                article={products.IS2}
                            />
                        )}

                        {/* Second insulation layer - second part (closer to core) */}
                        {products.IS2 && (
                            <Part
                                properties={properties}
                                product={sessionProduct}
                                drawblockwidth={drawBlockWidth}
                                partname={`${DRAWING_CONSTANTS.layerNames.insulation2Part2} isolatie`}
                                sponning={DRAWING_CONSTANTS.sponningTypes.inner}
                                isolation
                                isolation1
                                insideview={insideViewProperty}
                                depth={
                                    products.IS2
                                        ? calculateSponningHeight(
                                              products.IS2.zdimsize,
                                              DRAWING_CONSTANTS.sponningTypes.inner,
                                              false,
                                              properties,
                                              sessionProduct
                                          )
                                        : DRAWING_CONSTANTS.defaultInsulationDepth
                                }
                                article={products.IS2}
                            />
                        )}

                        {/* Core plate (kernplaat) - structural center of panel */}
                        {products.TL && (
                            <Part
                                properties={properties}
                                product={sessionProduct}
                                drawblockwidth={drawBlockWidth}
                                partname={DRAWING_CONSTANTS.layerNames.corePlate}
                                material={properties.kp_material}
                                insideview={insideViewProperty}
                                depth={products.TL ? products.TL.zdimsize : DRAWING_CONSTANTS.defaultLayerDepth}
                                article={products.TL}
                            />
                        )}

                        {/* First insulation layer - second part (closer to core) */}
                        <Part
                            properties={properties}
                            product={sessionProduct}
                            drawblockwidth={drawBlockWidth}
                            partname={`${DRAWING_CONSTANTS.layerNames.insulation1Part2}  isolatie`}
                            sponning={DRAWING_CONSTANTS.sponningTypes.outer}
                            isolation
                            isolation1={sponningSide === DRAWING_CONSTANTS.sponningTypes.outer}
                            isolation2={sponningSide !== DRAWING_CONSTANTS.sponningTypes.outer}
                            insideview={insideViewProperty}
                            singleisolation={properties.TL === 0}
                            depth={
                                products.IS1
                                    ? calculateSponningHeight(
                                          products.IS1.zdimsize,
                                          DRAWING_CONSTANTS.sponningTypes.outer,
                                          sponningSide === DRAWING_CONSTANTS.sponningTypes.outer,
                                          properties,
                                          sessionProduct,
                                          "is-1-2",
                                      )
                                    : DRAWING_CONSTANTS.defaultInsulationDepth
                            }
                            article={products.IS1}
                        />

                        {/* First insulation layer - first part (closer to outside) */}
                        <Part
                            properties={properties}
                            product={sessionProduct}
                            drawblockwidth={drawBlockWidth}
                            partname={`${DRAWING_CONSTANTS.layerNames.insulation1} isolatie`}
                            sponning={DRAWING_CONSTANTS.sponningTypes.outer}
                            isolation
                            isolation1={sponningSide !== DRAWING_CONSTANTS.sponningTypes.outer}
                            isolation2={sponningSide === DRAWING_CONSTANTS.sponningTypes.outer}
                            onlyarticle={products.IS1}
                            depth={
                                products.IS1
                                    ? calculateSponningHeight(
                                          products.IS1.zdimsize,
                                          DRAWING_CONSTANTS.sponningTypes.outer,
                                          sponningSide !== DRAWING_CONSTANTS.sponningTypes.outer,
                                          properties,
                                          sessionProduct,
                                          "is-1",
                                      )
                                    : DRAWING_CONSTANTS.defaultInsulationDepth
                            }
                            article={products.IS1}
                            insideview={insideViewProperty}
                        />

                        {/* Outer layer (buitenlaag) - outermost layer exposed to weather */}
                        <Part
                            properties={properties}
                            product={sessionProduct}
                            drawblockwidth={drawBlockWidth}
                            partname={DRAWING_CONSTANTS.layerNames.outerLayer}
                            sponning={DRAWING_CONSTANTS.sponningTypes.outer}
                            material={properties.bu_material}
                            insideview={insideViewProperty}
                            depth={products.BU ? products.BU.zdimsize : DRAWING_CONSTANTS.defaultLayerDepth}
                            colors={colors}
                            color={products.BU ? products.BU.color : DRAWING_CONSTANTS.defaultColor}
                            article={products.BU}
                        />
                    </div>

                    {/* Reference text for dimension context */}
                    <p
                        style={{
                            position: "absolute",
                            bottom: 0,
                            right: 10,
                            fontSize: 10,
                        }}
                    >
                        {text}
                    </p>
                </>
            )}
        </div>
    );
};

export default Drawing;
